/* eslint-disable max-lines */
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import AutorenewIcon from '@mui/icons-material/AutorenewOutlined';
import BusinessIcon from '@mui/icons-material/BusinessOutlined';
import CalendarTodayIcon from '@mui/icons-material/CalendarTodayOutlined';
import { createSelector } from 'reselect';
import { customFieldSchema } from '../../../schema/deal_schema';
import CustomInput from '../../reusable/input';
import CustomModal from '../../modals';
import { dealCustomFieldsUpdateThunk } from '../../../redux/thunks/deal';
import { formatDate } from '../../../helper';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import NoteRender from './notes';
import ReminderRender from './reminder';
import { styled } from '@mui/material/styles';
import { useState } from 'react';
import { AppDispatch, RootState } from '../../../redux/store';
import { Box, Button, Tab, Tabs, Typography } from '@mui/material';
import { Form, Formik } from 'formik';
import { toast, Zoom } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';

/**
 * @heading1: DealOverview Component
 * @paragraph: Renders a form to display and update custom fields for a deal.
 */

/**
 * @heading4: Styled components for Tabs and Tab elements
 */

const StyledTabs = styled(Tabs)({
    '& .MuiTabs-indicator': {
        backgroundColor: '#2979ff',
        height: 2
    }
});

const StyledTab = styled(Tab)({
    'textTransform': 'none',
    'fontSize': 14,
    'fontWeight': 500,
    'color': '#37474f',
    '&.Mui-selected': {
        color: '#2979ff'
    },
    'flex': 1
});

/**
 * @interface: DealOverviewProps
 * @paragraph: Props for the `DealOverviewModal` component.
 * @property: {boolean} open - Whether the modal is open or not.
 * @property: {function} onClose - Function to close the modal.
 * @property: {function} onTransfer - Function to trigger the transfer action.
 * @property: {function} onCloseDeal - Function to close the deal.
 */
interface DealOverviewProps {
    open: boolean;
    onClose: () => void;
    onTransfer: () => void;
    onCloseDeal: () => void;
}

/**
 * @interface: CustomField
 * @paragraph: Interface for custom fields used in the `CustomFieldRender` component.
 */
interface CustomField {
    customField1: string;
    customField2: string;
    customField3: string;
    customField4: string;
    customField5: string;
}

/**
 * @heading1: CustomFieldRender
 * @returns: {JSX.Element} The JSX element for the `DealOverviewModal` component.
 */
const CustomFieldRender: React.FC = () => {
    /**
     * @heading3: Variables
     * @ol:
     * initialValues: {CustomField} -  Initial form values based on the selected deal's custom fields.
     * dispatch: {AppDispatch} - The Redux dispatch function to trigger actions.
     * selectedDeal: An object containing the details from the Redux store.
     */
    const dispatch = useDispatch<AppDispatch>();
    const { selectedDeal, loaders } = useSelector((state: RootState) => state.deal);
    // Initial form values based on the selected deal's custom fields
    const initialValues: CustomField = {
        customField1: selectedDeal?.custom_field_1 || '',
        customField2: selectedDeal?.custom_field_2 || '',
        customField3: selectedDeal?.custom_field_3 || '',
        customField4: selectedDeal?.custom_field_4 || '',
        customField5: selectedDeal?.custom_field_5 || ''
    };

    /**
     * @heading3: Function
     * @heading4: Handles form submission for updating custom fields.
     * @param: {CustomField} values - The custom field values to update.
     */
    const handleSubmit = async (values: CustomField) => {
        try {
            await dispatch(dealCustomFieldsUpdateThunk({
                deal_id: selectedDeal?.id as number,
                custom_fields: {
                    custom_field_1: values.customField1,
                    custom_field_2: values.customField2,
                    custom_field_3: values.customField3,
                    custom_field_4: values.customField4,
                    custom_field_5: values.customField5
                }
            })).unwrap();
            toast.success('Updated Successfully', {
                position: 'top-right',
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: true,
                theme: 'dark',
                transition: Zoom
            });
        } catch (e) {
            //
        }
    };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={customFieldSchema}
            onSubmit={handleSubmit}
        >
            {({ values, handleChange, handleBlur, errors, touched }) => (
                <Form>
                    <Box display="flex" flexDirection="column" gap={1}>
                    {/* Custom field inputs */}
                    <CustomInput
                        value={values.customField1}
                        onChange={handleChange('customField1')}
                        onBlur={handleBlur('customField1')}
                        error={touched.customField1 && errors.customField1}
                        fullWidth
                        placeholder="Custom field 1..."
                        variant="outlined"
                    />
                    <CustomInput
                        value={values.customField2}
                        onChange={handleChange('customField2')}
                        onBlur={handleBlur('customField2')}
                        error={touched.customField2 && errors.customField2}
                        fullWidth
                        placeholder="Custom field 2..."
                        variant="outlined"
                    />
                    <CustomInput
                        value={values.customField3}
                        onChange={handleChange('customField3')}
                        onBlur={handleBlur('customField3')}
                        error={touched.customField3 && errors.customField3}
                        fullWidth
                        placeholder="Custom field 3..."
                        variant="outlined"
                    />
                    <CustomInput
                        value={values.customField4}
                        onChange={handleChange('customField4')}
                        onBlur={handleBlur('customField4')}
                        error={touched.customField4 && errors.customField4}
                        fullWidth
                        placeholder="Custom field 4..."
                        variant="outlined"
                    />
                    <CustomInput
                        value={values.customField5}
                        onChange={handleChange('customField5')}
                        onBlur={handleBlur('customField5')}
                        error={touched.customField5 && errors.customField5}
                        fullWidth
                        placeholder="Custom field 5..."
                        variant="outlined"
                    />
                    <Button sx={{
                        marginTop: '10px'
                    }} type="submit" variant="outlined" color="primary" fullWidth disabled={loaders.isUpdating}>
                        {loaders.isUpdating ? 'Saving...' : 'Save'}
                    </Button>
                    </Box>
                </Form>
                )}
                </Formik>
    );
};

const stateSelectDeal = (state: RootState) => state.deal.selectedDeal;
const stateReminderLoaders = (state: RootState) => state.reminder.loaders;
const stateNotesLoaders = (state: RootState) => state.note.loaders;
const stateDealLoaders = (state: RootState) => state.deal.loaders;


const selectedState = createSelector(
    [stateReminderLoaders, stateNotesLoaders, stateDealLoaders, stateSelectDeal],
    (reminderLoaders, notesLoaders, dealLoaders, selectedDeal) => ({
        reminderLoaders,
        notesLoaders,
        dealLoaders,
        selectedDeal
    })
);


/**
 * @heading1: DealOverviewModal
 * @paragraph: Displays an overview modal for a deal, including notes, reminders, and custom fields.
 */
const DealOverviewModal: React.FC<DealOverviewProps> = ({ open, onClose, onTransfer, onCloseDeal }: DealOverviewProps) => {
    // const dispatch = useDispatch<AppDispatch>();
    const { selectedDeal, reminderLoaders, notesLoaders, dealLoaders } = useSelector(selectedState);
    const { selectedPipeline } = useSelector((state: RootState) => state.pipeline);
    const [activeTab, setActiveTab] = useState(0);
    const isLoading = (reminderLoaders.isCreating || reminderLoaders.isDeleting || reminderLoaders.isLoading) ||
        (dealLoaders.isCreating || dealLoaders.isDeleting || dealLoaders.isLoading) ||
        (notesLoaders.isCreating || notesLoaders.isDeleting || notesLoaders.isLoading);

    // useEffect(() => {
    //     const fetchNotesAndReminders = async () => {
    //         try {
    //             await dispatch(getDealsNotesThunk(selectedDeal?.id as number)).unwrap();
    //             await dispatch(getDealsRemindersThunk(selectedDeal?.id as number)).unwrap();
    //         } catch (e) {
    //             //
    //         }
    //     };
    //     fetchNotesAndReminders();
    // }, []);

    /**
     * @heading3: Handles tab change for switching between Notes, Reminders, and Custom Fields.
     * @param: {React.SyntheticEvent} event - The tab change event.
     * @param: {number} newValue - The index of the newly selected tab.
     */
    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    return <CustomModal open={open} handleClose={isLoading ? () => null : onClose} title="Overview">
        {/* Deal title and company information */}
        <Box display="flex" justifyContent="space-between" mb={2}>
            <Typography variant="h6" display="flex" alignItems="center" maxWidth="210px !important">
                <HandshakeOutlinedIcon sx={{ fontSize: 16, marginRight: 1 }} /> {selectedDeal?.title}
            </Typography>
            <Typography variant="body2" color="textSecondary" display="flex" alignItems="center" maxWidth="210px !important" textOverflow="ellipsis" overflow="hidden">
                <BusinessIcon sx={{ fontSize: 16, marginRight: 1 }} />
                {selectedDeal?.company}
            </Typography>
        </Box>
        <Box display="flex" justifyContent="space-between" mb={2} textAlign="right">
            <Typography variant="h6" display="flex" alignItems="center">
                <AttachMoneyOutlinedIcon sx={{ fontSize: 16, marginRight: 1 }} /> {selectedPipeline?.deal_default_value}
            </Typography>
            <Typography variant="body2" color="textSecondary" display="flex" alignItems="center">
                <CalendarTodayIcon sx={{ fontSize: 16, marginRight: 1 }} />
                {formatDate(selectedDeal?.due_date || '2050-12-2')}
            </Typography>
        </Box>

        {/* Action Buttons */}
        <Box display="flex" gap={2} mb={2}>
            <Button
                variant="outlined"
                color="primary"
                startIcon={<AutorenewIcon />}
                fullWidth
                onClick={onTransfer}
            >
                Transfer
            </Button>
            <Button variant="contained" color="primary" onClick={onCloseDeal} fullWidth>
                {selectedDeal?.is_closed === 0 ? 'Close Deal' : 'Unclose Deal'}
            </Button>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <StyledTabs
                value={activeTab}
                onChange={handleTabChange}
                textColor="primary"
                indicatorColor="primary"
                centered
            >
                <StyledTab label="Notes" />
                <StyledTab label="Reminders" />
                <StyledTab label="Custom Field" />
            </StyledTabs></Box>

        {/* Tab Content */}
        <Box mt={1}>
            {activeTab === 0 && <NoteRender />}
            {activeTab === 1 && <ReminderRender />}
            {activeTab === 2 && <CustomFieldRender />}
        </Box>
    </CustomModal>;
};

export default DealOverviewModal;